import classNames from 'classnames';
import { IReportedIssueDetails } from 'src/store';
import { FC } from 'react';
import { Flex, Image, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { getLocalizedDateTime } from '@common/helpers/getCurrentDateAndTime';
import ActionBlock from '../ActionBlock';
import { Info } from '@geist-ui/icons';
import { capitalizeFirstLetter } from 'src/helpers/capitalizeFirstLetter';
import { Base64 } from 'js-base64';
import { getConcatIssueLabelsFull } from '@common/helpers/getConcatIssueLabels';
import { statusMap } from '@common/constants/statuses';
import SiteLink from '../SiteLink';

import styles from './styles.module.scss';
import { useMediaQuery } from '@mantine/hooks';

type IssueProps = {
  issue: IReportedIssueDetails | null;
  userRole?: 'userView' | 'view';
};

export const Issue: FC<IssueProps> = ({ issue, userRole = 'view' }) => {
  const { t } = useTranslation('issue');
  const [c] = useTranslation('widget');
  const [r] = useTranslation('reported');
  const concatenatedLabels = getConcatIssueLabelsFull(issue?.type || []);
  const lastFocusedElementsDecoded = issue?.lastFocusedElements
    ? JSON.parse(Base64.decode(issue?.lastFocusedElements))
    : [];
  const lastTenMouseMovementsDecoded = issue?.lastTenMouseMovements
    ? JSON.parse(Base64.decode(issue?.lastTenMouseMovements))
    : [];

  const lastFocusPositionsInDomDecoded = issue?.lastFocusPositionsInDom
    ? JSON.parse(Base64.decode(issue?.lastFocusPositionsInDom))
    : [];

  const currentStatus = issue?.archived
    ? statusMap.find((item) => item.value === 'archived')
    : statusMap.find((item) => item.value === issue?.status);

  const isMediumDevice = useMediaQuery('(max-width: 992px)');

  return (
    <Flex className={styles.description} direction={isMediumDevice ? 'column' : 'row'} gap={24}>
      {isMediumDevice && (
        <Title
          id={'mainContent'}
          className={classNames(styles['column1'], styles['title'])}
          weight={700}
          size={18}
          order={2}
          tabIndex={-1}>
          {t('report')} {issue?.reportNumber} {t('for')}{' '}
          {issue?.createdDate ? getLocalizedDateTime(new Date(issue.createdDate)) : ''}
        </Title>
      )}
      <Image
        alt={`${t('websiteImageAlt')}`}
        radius={4}
        height={isMediumDevice ? 100 : 200}
        width={isMediumDevice ? 150 : 370}
        src={issue?.screenshotUrl}></Image>
      <Flex>
        <div className={styles['grid-container']}>
          {!isMediumDevice && (
            <Title
              id={'mainContent'}
              className={classNames(styles['column1'], styles['title'])}
              weight={700}
              size={18}
              order={2}
              tabIndex={-1}>
              {t('report')} {issue?.reportNumber} {t('for')}{' '}
              {issue?.createdDate ? getLocalizedDateTime(new Date(issue.createdDate)) : ''}
            </Title>
          )}
          <div className={classNames(styles['column1'], styles['title'])}>{concatenatedLabels}</div>
          <div className={styles['column1']}>
            <div className={classNames([styles.status])} style={{ color: currentStatus?.color }}>
              {currentStatus?.label && r(currentStatus?.label)}
            </div>

            <div className={classNames([styles.statusInfo])}>
              <Info size={20} />
              <span>{currentStatus?.description && r(currentStatus?.description)}</span>
            </div>
          </div>
          <div className={styles['column1']}>
            <ActionBlock userRole={userRole} item={issue || undefined} />
          </div>
          <Title order={3} size={16} className={classNames(styles['column1'], styles['title'])} weight={700}>
            {t('technicalDetails')}
          </Title>
          <Text className={styles['column1']} w={210}>
            {t('userIdLabel')}
          </Text>{' '}
          <div className={styles['column2']}>{issue?.userId}</div>
          <Text className={styles['column1']} w={210}>
            {t('operatingSystemLabel')}
          </Text>{' '}
          <div className={styles['column2']}>{capitalizeFirstLetter(`${issue?.operatingSystem}`)}</div>
          <Text className={styles['column1']} w={210}>
            {t('description')}
          </Text>{' '}
          <div className={styles['column2']}>{ issue?.description ? capitalizeFirstLetter(`${issue?.description}`)  : '' }</div>
          <Text className={styles['column1']} w={210}>
            {t('languageLabel')}
          </Text>{' '} 
          <div className={styles['column2']}>{issue?.language}</div>
          <Text className={styles['column1']} w={210}>
            {t('geolocationLabel')}
          </Text>{' '}
          <div className={styles['column2']}>
            {issue?.latitude} {issue?.longitude}
          </div>
          <Text className={styles['column1']} w={210}>
            {t('screenSizeLabel')}
          </Text>{' '}
          <div className={styles['column2']}>{issue?.screenSize}</div>
          <Text className={styles['column1']} w={210}>
            {t('browserLabel')}
          </Text>{' '}
          <div className={styles['column2']}>{capitalizeFirstLetter(`${issue?.browser}`)}</div>
          <Text className={styles['column1']} w={210}>
            {t('browserIdLabel')}
          </Text>{' '}
          <div className={styles['column2']}>{issue?.browserId}</div>
          <Text className={styles['column1']} w={210}>
            {t('fontsAvailableLabel')}
          </Text>{' '}
          <div className={styles['column2']}>
            {issue?.fontsAvailable?.map((item) => (
              <div key={item.fontId}>{item.displayName}</div>
            ))}
          </div>
          <Text className={styles['column1']} w={210}>
            {t('activePluginsLabel')}
          </Text>
          <div className={styles['column2']}>
            {issue?.activePlugins?.map((item) => (
              <div key={item}>{item}</div>
            ))}
          </div>
          <Text className={styles['column1']} w={210}>
            {t('cpuAndRamLabel')}
          </Text>{' '}
          <div className={styles['column2']}>{issue?.cpuAndRam}</div>
          <Text className={styles['column1']} w={210}>
            {t('widgetSettingsLabel')}
          </Text>{' '}
          <div className={styles['column2']}>
            {issue?.widgetSettings
              ? issue?.widgetSettings?.map((item, idx) => (
                  <span key={item}>
                    {c(`${item}Initial`)}
                    {idx < issue?.widgetSettings.length - 1 ? '; ' : ''}
                  </span>
                ))
              : r('emptyWidgetSettings')}
          </div>
          <Title order={3} size={16} className={classNames(styles['column1'], styles['title'])} weight={700}>
            {t('websiteInformation')}
          </Title>
          <div className={styles['column1']}>
            <Text w={210}>{t('pageLabel')}</Text>{' '}
          </div>
          <div className={styles['column2']}>
            <SiteLink website={issue?.websiteUrl} />
          </div>
          <Text className={styles['column1']} w={210}>
            {t('previousPageLabel')}
          </Text>{' '}
          <div className={classNames(styles['column2'], styles['previous-page'])}>
            <SiteLink website={issue?.previousPage?.page} />
          </div>
          <Text className={styles['column1']} w={210}>
            {t('lastFourPagesLabel')}
          </Text>
          <div className={styles['column2']}>
            {issue?.lastFourPages?.map((item) => (
              <Text py={8} key={item.timestamp}>
                {getLocalizedDateTime(new Date(item.timestamp))} -{item.page}
              </Text>
            ))}
          </div>
          <Text className={styles['column1']} w={210}>
            {t('lastFocusedElementsLabel')}
          </Text>
          <div className={styles['column2']}>
            {lastFocusedElementsDecoded?.map((item: any) => (
              <Text py={8} key={item}>
                {item}
              </Text>
            ))}
          </div>
          <Text className={styles['column1']} w={210}>
            {t('lastTenMouseMovementsLabel')}
          </Text>
          <div className={styles['column2']}>
            {lastTenMouseMovementsDecoded?.map((item: any) => (
              <Text py={8} key={item}>
                {item}
              </Text>
            ))}
          </div>
          <Text className={styles['column1']} w={210}>
            {t('lastFocusPositionsInDomLabel')}
          </Text>{' '}
          <div className={styles['column2']}>
            {lastFocusPositionsInDomDecoded?.map((item: any) => (
              <Text py={8} key={item.timestamp}>
                {getLocalizedDateTime(new Date(item.timestamp))} - {item.page}
              </Text>
            ))}
          </div>
        </div>
      </Flex>
    </Flex>
  );
};
